/**
 * Person identity for the CRM — the single rule answering "is this the same
 * human?" for every lead source (ARCH-040 §Identity, to be written in this same
 * PR; until it lands the rationale below is the only record).
 *
 * It lives in its own module because the lead push, the contacts sync and both
 * qualification webhooks will all need the same answer. Only the lead push
 * exists today; the other three are later steps of this integration, so treat
 * "four call sites" as the reason for the shape, not as a description of the
 * tree.
 *
 * WHY `users.id` IS NOT THE ANSWER. 963 of 2009 accounts are ANONYMOUS Supabase
 * auth users: the funnel signs a guest in anonymously and collects an email
 * later. That uuid is stable per session lineage, not per person — and the live
 * data shows the consequence outright: 14 email addresses sit on 28 different
 * user rows, 13 of those pairs being one anonymous row plus one real row for the
 * same human (measured 2026-08-28).
 *
 * The ladder, in order:
 *   1. `user:<uuid>`   — ONLY for a non-anonymous account, where the uuid IS the
 *                        person and survives them changing their email.
 *   2. `mail:<addr>`   — lower-cased, taken from the SOURCE ROW rather than the
 *                        session, so a guest typing the same address twice lands
 *                        on one card.
 *   3. `phone:<E.164>` — the chatbot accepts callbacks with a phone and no email
 *                        (12 of 63 live callbacks).
 *   4. `null`          — no way to reach this person at all, so no lead. Callers
 *                        REFUSE rather than create an uncontactable card.
 *
 * WHAT ONE KEY DOES NOT DO, stated plainly because the shape invites the
 * opposite assumption: a single key does NOT collapse the anonymous/real pair
 * above. That person's anonymous row yields `mail:…` and their real row yields
 * `user:…` — two different keys. Collapsing them is why `personKeyCandidates`
 * exists and why `findOpenLeadForPerson` searches EVERY form rather than the
 * one that happens to be current.
 */

import { isCanonicalUuid } from "@oekopunktemarkt/entity-pointers";
import { normalizePhone } from "./phone";
export { normalizePhone } from "./phone";

/** Which rung of the ladder produced a key. */
export type PersonKeyKind = "user" | "mail" | "phone";

/**
 * The canonical E.164 for a person, preferring Postgres' own normalisation.
 *
 * THIS IS THE ANSWER TO "WHICH NORMALISER?". There are several in the codebase,
 * and the CRM must use the one the rest of the platform matches on:
 * `users.phone_e164` is a GENERATED column, `normalize_phone_e164(phone)`, and
 * `UserRepository.findByPhone` matches on it. Re-deriving E.164 in JS maintains
 * a second answer to "is this the same person?" — which is precisely the
 * question this module exists to answer once.
 *
 * The stored value is still gated on `isValidPhoneNumber`, and that is not
 * belt-and-braces: `validators/phone.ts` says outright that the SQL function
 * NORMALIZES WITHOUT JUDGING — "there is deliberately no SQL counterpart" to the
 * length check. So a too-short number reaches `phone_e164` unchanged, and
 * writing one into Monday's phone column makes Monday reject the ENTIRE mutation
 * and lose the whole lead with it — email, region, volume and price included.
 *
 * `normalizePhone` is idempotent on a string that is already E.164 (it re-runs
 * `formatPhoneNumber`, whose "+" branch is a no-op), so both paths provably
 * produce the same shape rather than two shapes that usually agree.
 *
 * Falls back to the raw column for sources that never reach `users` at all — a
 * number typed into the chat for a Rückruf, or into the contact form.
 */
export function canonicalPhone(input: {
  phone?: string | null;
  phone_e164?: string | null;
}): string | null {
  return normalizePhone(input.phone_e164) ?? normalizePhone(input.phone);
}

export interface PersonKeyInput {
  /**
   * The account behind this artefact, when there is one. Callbacks and contact
   * forms have no user row at all; funnel guests have an anonymous one.
   */
  user?: { id: string; is_anonymous?: boolean | null } | null;
  /** The address on the SOURCE ROW — not the session's. */
  email?: string | null;
  /** Whatever the source row holds — national or international spelling. */
  phone?: string | null;
  /** Postgres' E.164 for this person, where the source is a `users` row. */
  phone_e164?: string | null;
}

/**
 * EVERY key this person could be filed under, most durable first.
 *
 * This is the ladder itself; `personKeyOf` is just its first rung. They are one
 * function on purpose — the projections STAMP with `personKeyOf` and the lookup
 * SEARCHES with this, so two copies of the rules would let the key we write
 * drift from the keys we search, and the dedupe would fail open with no test
 * able to see it (each would only ever be tested against itself).
 *
 * Returning all of them is what collapses duplicates: the same human's
 * anonymous funnel session yields `mail:…` while their registered account
 * yields `user:…`, so a lookup that tried only the current key would file them
 * twice — the outcome the module header cites 13 live cases of.
 */
export function personKeyCandidates(input: PersonKeyInput): string[] {
  const keys: string[] = [];
  const { user } = input;
  if (user?.id && user.is_anonymous === false) keys.push(`user:${user.id}`);

  const email = input.email?.trim().toLowerCase();
  if (email) keys.push(`mail:${email}`);

  // Same normaliser question as everywhere else — see `canonicalPhone`. A key
  // built from a differently-normalised number files the same human twice.
  const phone = canonicalPhone(input);
  if (phone) keys.push(`phone:${phone}`);

  return keys;
}

/**
 * The dedupe key for one person, or null when we hold no way to reach them.
 *
 * `is_anonymous` is read from `public.users`, which mirrors
 * `auth.users.is_anonymous`. That is a DB invariant, not a coincidence: the sync
 * trigger in `20260327120000_redesign_stitch.sql` and `handle_new_user`
 * (re-declared in `20260824115857_…`) maintain it on every insert and update, so
 * the projection can stay pure instead of making an `auth.admin` round-trip.
 * (Spot-checked 2026-08-28: 2009 rows, 0 nulls, 0 disagreements.)
 *
 * A NULL `is_anonymous` is treated as anonymous — the safe direction, since the
 * cost is a `mail:` key rather than a `user:` one, while the reverse would mint
 * a durable key from a throwaway uuid.
 */
export function personKeyOf(input: PersonKeyInput): string | null {
  return personKeyCandidates(input)[0] ?? null;
}

/** Which rung produced `key`, or null when it is not one of ours. */
/**
 * True when a key is one of ours AND its payload is actually usable.
 *
 * `personKeyKind` only reads the prefix, which is right for classifying but
 * wrong for trusting: `ÖPM-Schlüssel` is a PLAIN TEXT column on a board sales
 * edits by hand, so `user:5f3a1c` (a truncated paste) or a uuid with a trailing
 * space classify as `user` and then make `Pointers.opm.user()` THROW — before
 * any CRM call, from inside a loop, with no note on the card and no way for the
 * caller to tell a bad key from a bad network.
 *
 * The uuid rule comes from `isCanonicalUuid`, exported by the pointer package
 * itself, so this check and the builder it predicts cannot disagree. A local
 * regex here was stricter than the package's — it pinned the version and
 * variant nibbles — and would have refused ids the builder accepts, telling a
 * rep their data was unreadable when it was fine.
 */
export function isUsableKey(key: string | null | undefined): boolean {
  if (!key?.trim()) return false;
  const kind = personKeyKind(key.trim());
  if (!kind) return false;

  const value = key.trim().slice(kind.length + 1);
  if (!value) return false;
  // `user:` is the only rung whose payload has a required SHAPE.
  if (kind === "user") return isCanonicalUuid(value);
  return true;
}

export function personKeyKind(key: string): PersonKeyKind | null {
  if (key.startsWith("user:")) return "user";
  if (key.startsWith("mail:")) return "mail";
  if (key.startsWith("phone:")) return "phone";
  return null;
}
